import { z } from "zod";
const safeIdentifierSchema = z.string().trim().min(1).max(240).regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/u);
const exactNonnegativeIntegerSchema = z.string().regex(/^(0|[1-9][0-9]*)$/u);
const timestampSchema = z.number().int().nonnegative();
export const billingAccountStatusSchema = z.enum([
    "pending",
    "active",
    "suspended",
    "closed",
]);
export const billingModelSchema = z.enum(["legacy_observe", "twin_catalog_v1", "twin_credits_v1"]);
export const billingUsageDeliveryModeSchema = z.enum(["local_shadow", "metronome"]);
export const billingEnforcementModeSchema = z.enum(["observe", "shadow", "enforce"]);
export const billingPlanKeySchema = z.enum([
    "twin-team",
    "twin-core",
    "twin-scale",
    "twin-enterprise",
]);
export const billingCreditPackKeySchema = z.enum([
    "credits-starter",
    "credits-team",
    "credits-scale",
]);
export const billingCatalogKeySchema = z.enum([
    "twin-team-monthly",
    "twin-team-guest-monthly",
    "twin-core-monthly",
    "twin-scale-monthly",
    "twin-enterprise-monthly",
    "credits-starter-monthly",
    "credits-team-monthly",
    "credits-scale-monthly",
    "credits-starter-top-up",
    "credits-team-top-up",
    "credits-scale-top-up",
    "fde-day-monthly",
    "fde-day-one-off",
]);
export const billingSubscriptionStatusSchema = z.enum([
    "none",
    "pending_payment",
    "active",
    "past_due",
    "scheduled_to_cancel",
    "cancelled",
    "suspended",
]);
export const billingPaymentStateSchema = z.enum([
    "none",
    "pending",
    "paid",
    "failed",
    "refunded",
    "disputed",
]);
export const billingSubscriptionSelectionSchema = z.object({
    plan: billingPlanKeySchema,
    seatQuantity: z.number().int().positive().max(2_147_483_647).optional(),
    // Edit-guest seats on a Team subscription; absent means none.
    guestSeatQuantity: z.number().int().min(0).max(2_147_483_647).optional(),
    creditPack: billingCreditPackKeySchema.nullable(),
    recurringFdeDays: z.number().int().min(0).max(2_147_483_647).default(0),
    idempotencyKey: safeIdentifierSchema,
}).strict().superRefine((selection, context) => {
    if ((selection.plan === "twin-team") !== (selection.seatQuantity !== undefined)) {
        context.addIssue({ code: "custom", path: ["seatQuantity"], message: "Team requires a seat quantity; dedicated plans use their own seat terms." });
    }
    if (selection.plan !== "twin-team" && (selection.guestSeatQuantity ?? 0) > 0) {
        context.addIssue({ code: "custom", path: ["guestSeatQuantity"], message: "Only Team sells guest seats." });
    }
    if (selection.plan !== "twin-team" && selection.recurringFdeDays > 20) {
        context.addIssue({ code: "custom", path: ["recurringFdeDays"], message: "Dedicated recurring engineering exceeds the current catalogue limit." });
    }
});
export const billingPendingSubscriptionChangeSchema = z.object({
    plan: billingPlanKeySchema,
    seatQuantity: z.number().int().positive().nullable().optional(),
    guestSeatQuantity: z.number().int().nonnegative().optional(),
    creditPack: billingCreditPackKeySchema.nullable(),
    // Team engineering days are uncapped; dedicated selections keep their own limit.
    recurringFdeDays: z.number().int().min(0),
    effectiveAt: timestampSchema,
}).strict();
export const billingSubscriptionChangeResponseSchema = z.object({
    state: z.enum(["scheduled", "cancelled"]),
    effectiveAt: timestampSchema,
}).strict();
export const billingTopUpSelectionSchema = z.object({
    creditPack: billingCreditPackKeySchema,
    idempotencyKey: safeIdentifierSchema,
}).strict();
export const billingPortalRequestSchema = z.object({}).strict();
export const billingPortalResponseSchema = z.object({
    portalUrl: z.string().url().refine((raw) => {
        const url = new URL(raw);
        return url.protocol === "https:" && url.hostname === "billing.stripe.com"
            && !url.username && !url.password && !url.port;
    }, "Expected a Stripe-hosted billing portal URL."),
}).strict();
export const billingCheckoutResponseSchema = z.object({
    checkoutUrl: z.string().url().refine((value) => new URL(value).protocol === "https:"),
    paymentState: z.literal("pending"),
}).strict();
export const billingCatalogItemSchema = z.object({
    key: billingCatalogKeySchema,
    productKind: z.enum([
        "twin_plan",
        "recurring_credit_pack",
        "top_up_credit_pack",
        "recurring_fde",
        "one_off_fde",
        "team_guest_seat",
    ]),
    cadence: z.enum(["monthly", "one_time"]),
    unitAmountMinor: exactNonnegativeIntegerSchema,
    creditMicrounits: exactNonnegativeIntegerSchema.nullable(),
    displayName: z.string().trim().min(1).max(160),
    description: z.string().trim().max(500).nullable(),
}).strict();
export const billingCatalogSchema = z.object({
    catalogVersion: z.string().trim().min(1).max(120),
    currency: z.string().regex(/^[A-Z]{3}$/u),
    items: z.array(billingCatalogItemSchema),
}).strict();
/** Whether the tenant prices Max's model, so members may switch a session to it. */
export const billingMaxModeSchema = z.object({
    available: z.boolean(),
}).strict();
export const billingBalanceSchema = z.object({
    availableCreditMicrounits: exactNonnegativeIntegerSchema,
    ratedCreditMicrounits: exactNonnegativeIntegerSchema,
    reservedCreditMicrounits: exactNonnegativeIntegerSchema,
    pendingUsageCreditMicrounits: exactNonnegativeIntegerSchema,
    freshAt: timestampSchema.nullable(),
    state: z.enum(["fresh", "stale", "unavailable"]),
}).strict();
export const billingDiscountSchema = z.object({
    basisPoints: z.number().int().min(1).max(9900),
    startsAt: timestampSchema,
    endsAt: timestampSchema,
}).strict();
// Token counts are given only where usage is observed, not charged: beside the credits an account is charged,
// they would let a credit price be read back into a rate per token.
export const billingObservedUsageSchema = z.object({
    modelCalls: exactNonnegativeIntegerSchema,
    inputTokens: exactNonnegativeIntegerSchema.optional(),
    outputTokens: exactNonnegativeIntegerSchema.optional(),
    reasoningTokens: exactNonnegativeIntegerSchema.optional(),
    cacheReadTokens: exactNonnegativeIntegerSchema.optional(),
    cacheWriteTokens: exactNonnegativeIntegerSchema.optional(),
    lastOccurredAt: timestampSchema.nullable(),
}).strict();
export const billingUsageTimelineDaysSchema = z.union([
    z.literal(7),
    z.literal(30),
    z.literal(90),
]);
export const billingUsageTimelineQuerySchema = z.object({
    days: z.coerce.number().pipe(billingUsageTimelineDaysSchema).default(30),
}).strict();
// Tokens only where usage is observed, not charged, as in billingObservedUsageSchema.
export const billingUsageTimelinePointSchema = z.object({
    bucketStart: timestampSchema,
    modelCalls: exactNonnegativeIntegerSchema,
    inputTokens: exactNonnegativeIntegerSchema.optional(),
    outputTokens: exactNonnegativeIntegerSchema.optional(),
}).strict();
// A Team plan's credits through the paid period: what was left at its start, after each day's use and after each
// top-up, in time order. It follows the meter's rules, so its last point is the balance the summary reports.
// Credits only, never tokens.
export const billingCreditDrawdownSchema = z.object({
    periodStart: timestampSchema,
    periodEnd: timestampSchema,
    grantedCreditMicrounits: exactNonnegativeIntegerSchema,
    usedCreditMicrounits: exactNonnegativeIntegerSchema,
    points: z.array(z.object({
        at: timestampSchema,
        availableCreditMicrounits: exactNonnegativeIntegerSchema,
    }).strict()).min(1).max(200),
    // The period's allowance, then each top-up, when it was added.
    grants: z.array(z.object({ at: timestampSchema, creditMicrounits: exactNonnegativeIntegerSchema }).strict()).min(1).max(60),
}).strict();
export const billingUsageTimelineSchema = z.object({
    days: billingUsageTimelineDaysSchema,
    startAt: timestampSchema,
    endAt: timestampSchema,
    points: z.array(billingUsageTimelinePointSchema).max(90),
    // Given for a Team plan with a paid period; absent otherwise and on older responses.
    creditDrawdown: billingCreditDrawdownSchema.optional(),
}).strict();
export const billingSummarySchema = z.object({
    accountStatus: billingAccountStatusSchema,
    billingModel: billingModelSchema,
    usageDeliveryMode: billingUsageDeliveryModeSchema,
    enforcementMode: billingEnforcementModeSchema,
    subscriptionStatus: billingSubscriptionStatusSchema,
    paymentState: billingPaymentStateSchema,
    plan: billingPlanKeySchema.nullable(),
    seatQuantity: z.number().int().positive().nullable().default(null),
    // Present for funded Team capacity; absent on older summary responses.
    paidSeatQuantity: z.number().int().nonnegative().nullable().optional(),
    paidSeatValidUntil: timestampSchema.nullable().optional(),
    // True while Stripe collects a renewal: access continues on the last paid seats; AI waits for payment.
    paidSeatRenewalPending: z.boolean().optional(),
    // Team Edit-guest seats: on the subscription, and paid for the current period. Absent before guest billing.
    guestSeatQuantity: z.number().int().nonnegative().optional(),
    paidGuestSeatQuantity: z.number().int().nonnegative().optional(),
    creditPack: billingCreditPackKeySchema.nullable(),
    recurringFdeDays: z.number().int().min(0),
    currentPeriodStart: timestampSchema.nullable(),
    currentPeriodEnd: timestampSchema.nullable(),
    cancelAtPeriodEnd: z.boolean(),
    pendingChange: billingPendingSubscriptionChangeSchema.nullable(),
    balance: billingBalanceSchema,
    discount: billingDiscountSchema.nullable(),
    observedUsage: billingObservedUsageSchema,
    warning: z.enum(["none", "low", "critical", "exhausted", "billing_error"]),
    canManageBilling: z.boolean(),
    // Whether this server is the shared Team deployment or an organisation's own. Absent on older summary responses.
    teamDeployment: z.enum(["shared", "dedicated"]).optional(),
    // Email domains of the people who support this server's customers. They work in the
    // organisation without taking a paid Team seat. Absent where the server names none.
    supportEmailDomains: z.array(z.string().trim().min(1).max(253)).max(20).optional(),
    // Stripe's payment page for a first payment that was started and has not gone through, while
    // Stripe still holds it open. Given only to people who manage billing.
    unfinishedCheckoutUrl: z.string().url().max(2048).optional(),
    // True only when this deployment can sell the Twin catalogue: the account is
    // on the catalogue model and the control plane holds a catalogue version plus
    // complete Stripe Checkout configuration for the account's Stripe mode.
    checkoutAvailable: z.boolean(),
}).strict();
export const billingInvoiceStateSchema = z.enum([
    "pending",
    "paid",
    "failed",
    "voided",
    "refunded",
    "disputed",
]);
const stripeInvoiceUrlSchema = z.string().url().refine((value) => {
    const url = new URL(value);
    return url.protocol === "https:"
        && (url.hostname === "stripe.com" || url.hostname.endsWith(".stripe.com"));
}, "Invoice links must use Stripe HTTPS hosts.");
export const billingInvoiceSchema = z.object({
    invoiceId: safeIdentifierSchema,
    invoiceNumber: z.string().trim().min(1).max(255).nullable(),
    state: billingInvoiceStateSchema,
    currency: z.string().regex(/^[A-Z]{3}$/u),
    amountDueMinor: exactNonnegativeIntegerSchema,
    amountPaidMinor: exactNonnegativeIntegerSchema,
    amountRefundedMinor: exactNonnegativeIntegerSchema,
    disputedAmountMinor: exactNonnegativeIntegerSchema,
    invoiceDate: timestampSchema,
    paidAt: timestampSchema.nullable(),
    servicePeriodStart: timestampSchema.nullable(),
    servicePeriodEnd: timestampSchema.nullable(),
    hostedInvoiceUrl: stripeInvoiceUrlSchema.nullable(),
    invoicePdfUrl: stripeInvoiceUrlSchema.nullable(),
}).strict();
export const listBillingInvoicesQuerySchema = z.object({
    cursor: z.string().trim().min(1).max(1_024).optional(),
    limit: z.coerce.number().int().min(1).max(50).default(12),
}).strict();
export const listBillingInvoicesResponseSchema = z.object({
    invoices: z.array(billingInvoiceSchema).max(50),
    nextCursor: z.string().min(1).max(1_024).nullable(),
}).strict();
const optionalUsageCountSchema = z.number().int().nonnegative().optional();
const optionalCostSchema = z.number().int().nonnegative().optional();
const billableUsageIdentitySchema = z.object({
    sourceId: safeIdentifierSchema,
    occurredAt: timestampSchema,
    runId: safeIdentifierSchema.optional(),
    sessionId: safeIdentifierSchema.optional(),
    attemptNumber: z.number().int().nonnegative().optional(),
    callIndex: z.number().int().nonnegative().optional(),
    region: z.string().trim().min(1).max(120).optional(),
    providerRequestId: z.string().trim().min(1).max(500).optional(),
    providerCostMicrousd: optionalCostSchema,
    estimatedCostMicrousd: optionalCostSchema,
    pricingVersion: z.string().trim().min(1).max(160).optional(),
    estimatedCreditMicrounits: z.number().int().nonnegative().optional(),
    rawDimensions: z.record(z.string(), z.union([
        z.string().max(500),
        z.number().finite(),
        z.boolean(),
    ])).default({}),
});
export const billableModelUsageSchema = billableUsageIdentitySchema.extend({
    kind: z.literal("model_call"),
    provider: z.string().trim().min(1).max(120),
    model: z.string().trim().min(1).max(240),
    runtimeRole: z.string().trim().min(1).max(120),
    inputTokens: optionalUsageCountSchema,
    outputTokens: optionalUsageCountSchema,
    reasoningTokens: optionalUsageCountSchema,
    cacheReadTokens: optionalUsageCountSchema,
    cacheWriteTokens: optionalUsageCountSchema,
}).strict().refine((value) => [
    value.inputTokens,
    value.outputTokens,
    value.reasoningTokens,
    value.cacheReadTokens,
    value.cacheWriteTokens,
].some((count) => count !== undefined), {
    message: "Billable model usage requires at least one token quantity.",
});
export const billableToolUsageSchema = billableUsageIdentitySchema.extend({
    kind: z.literal("tool_call"),
    toolProvider: z.string().trim().min(1).max(120),
    toolName: z.string().trim().min(1).max(240),
    unit: z.string().trim().min(1).max(120),
    quantity: z.number().positive().finite(),
}).strict();
export const billableUsageSchema = z.union([
    billableModelUsageSchema,
    billableToolUsageSchema,
]);
/** Member seats include credits; Edit-guest seats are priced separately and include none. */
export const billingSeatKindSchema = z.enum(["member", "guest"]);
export const billingSeatQuoteRequestSchema = z.object({
    seatQuantity: z.number().int().min(1).max(2_147_483_647),
    seatKind: billingSeatKindSchema.optional(),
    idempotencyKey: safeIdentifierSchema,
}).strict();
export const billingSeatQuoteResponseSchema = z.object({
    quoteId: safeIdentifierSchema,
    seatKind: billingSeatKindSchema.optional(),
    beforeSeats: z.number().int().nonnegative(),
    seatQuantity: z.number().int().positive(),
    chargeMinor: exactNonnegativeIntegerSchema,
    currency: z.string().regex(/^[A-Z]{3}$/u),
    includedCreditMicrounits: exactNonnegativeIntegerSchema,
    prorationAt: timestampSchema,
    expiresAt: timestampSchema,
}).strict();
export const billingSeatConfirmRequestSchema = z.object({ quoteId: safeIdentifierSchema }).strict();
export const billingSeatConfirmResponseSchema = z.object({
    quoteId: safeIdentifierSchema,
    status: z.enum(["paid", "payment_required", "failed"]),
    invoiceUrl: stripeInvoiceUrlSchema.nullable(),
}).strict();
export const billingPendingSeatQuoteResponseSchema = z.object({
    quote: billingSeatQuoteResponseSchema.extend({
        status: z.enum(["submitting", "payment_required"]),
    }).strict().nullable(),
}).strict();
